import { Button, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';

import { unitsApiKeyStore } from '../../../../api/units-api-key-store';
import { SiTrackGrid } from '../../../../components/si-track-grid/si-track-grid';
import { columns } from './lots-grid-columns';
import { GuardedComponent } from '../../../../components/guarded-component';
import { UserPermissionName } from '../../../../models/user-permission-name';
import { useEffect, useState } from 'react';
import { CreateWorkorderDialog } from '../../../workorders/create-workorder/create-workorder-dialog';
import { useAuthorize } from '../../../../hooks/use-authorize';
import { gridApiKeyStore } from '../../../../api/grid-api-key-store';
import { useLoadData } from '../../../../hooks/useLoadData';
import { ApiResponse } from '../../../../models/api-response';
import { Unit } from 'chart.js/dist/scales/scale.time';
import { useSaveData } from '../../../../hooks/useSaveData';
import { GridColumnConfig, GridPreference } from '../../../../models/gridCoumnConfig';
import { State } from '@progress/kendo-data-query';
import useDebounce from '../../../../hooks/use-debounce';

interface ProjectsTabProps {
  projectID: number;
  isNotCompleted: boolean;
}

export const LotsTab = ({ projectID, isNotCompleted }: ProjectsTabProps) => {
  const navigate = useNavigate();
  const redirectCreateLot = () => {
    navigate(`/projects/edit/${projectID}?tab=buildings`);
  };

  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [unitData, setUnitData] = useState<any>();

  const handleAddWorkOrder = (data: any) => {
    setUnitData(data);
    setIsFormOpen(true);
  };

  const handleDialogClose = () => {
    setIsFormOpen(false);
  };

  const addWorkOrderElement = (dataItem: any) => {
    const isAddWorkorderPermission = useAuthorize([UserPermissionName.ADD_WORKORDER]);
    return (
      <>
        {isNotCompleted && (
          <GuardedComponent requiredPermissions={[UserPermissionName.ADD_WORKORDER]}>
            <IconButton aria-label="view" onClick={() => handleAddWorkOrder(dataItem?.dataItem)}>
              <Tooltip title={<Typography variant="body1">{'Add Work Order'}</Typography>}>
                <AddCircleOutlineOutlinedIcon style={{ color: '$primary-color' }} />
              </Tooltip>
            </IconButton>
          </GuardedComponent>
        )}
        {!isAddWorkorderPermission && isNotCompleted && (
          <Tooltip title={<Typography variant="body1">{'you do not have add work order permission'}</Typography>}>
            <IconButton aria-label="view">
              <AddCircleOutlineOutlinedIcon style={{ color: 'gray' }} />
            </IconButton>
          </Tooltip>
        )}
        {!isNotCompleted && (
          <Tooltip title={<Typography variant="body1">{'project is completed'}</Typography>}>
            <IconButton aria-label="view">
              <AddCircleOutlineOutlinedIcon style={{ color: 'gray' }} />
            </IconButton>
          </Tooltip>
        )}
      </>
    );
  };

  // -------------------------------------------------Grid Layout------------------------------

  const {
    isLoading: isLoadingGridLayout,
    data: gridLayouts,
    refetch: refetchGridLayout,
  } = useLoadData<ApiResponse<GridPreference>>(gridApiKeyStore.getGridLayout('Project Details', 'Unit'));

  const {
    isLoading,
    data: units,
    refetch,
  } = useLoadData<ApiResponse<Unit[]>>(unitsApiKeyStore.getProjectUnits(projectID));

  const { mutateAsync } = useSaveData<any>(gridApiKeyStore.saveGridLayOut());
  const [gridConfig, setGridConfig] = useState<{
    dataState: State | undefined;
    columns: any[] | undefined;
  }>();

  const [filteredUnits, setFilteredUnits] = useState<any[]>([]);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  useEffect(() => {
    const filteredUnitList = units?.data?.filter((unit) =>
      Object.values(unit).some(
        (value) => value && value.toString().toLowerCase().includes(debouncedSearchTerm.toLowerCase())
      )
    );
    setFilteredUnits(filteredUnitList || []);
  }, [debouncedSearchTerm, units?.data]);

  const handleInputChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleSaveLayout = (gridDataState: State, gridColumns: GridColumnConfig[]) => {
    const data = {
      filterJSON: JSON.stringify(gridDataState),
      columnJSON: JSON.stringify(gridColumns),
      gridName: 'Unit',
      pageName: 'Project Details',
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  const handleResetLayout = () => {
    const data = {
      filterJSON: null,
      columnJSON: null,
      gridName: 'Unit',
      pageName: 'Project Details',
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  useEffect(() => {
    if (!gridLayouts?.data) return;

    try {
      const filterData = gridLayouts.data.filterJSON ? JSON.parse(gridLayouts.data.filterJSON) : null;

      const columnJson = gridLayouts.data.columnJSON ? JSON.parse(gridLayouts.data.columnJSON) : null;

      // if (filterData && columnJson) {
      setGridConfig({
        columns: columnJson,
        dataState: filterData,
      });
      // }
    } catch (error) {
      console.log('Error parsing grid layout JSON:', error);
    }
  }, [gridLayouts?.data]);

  return (
    <>
      <div className="borderTop">
        {isNotCompleted && (
          <GuardedComponent requiredPermissions={[UserPermissionName.ADD_UNIT]}>
            <Button
              className="innerTab-button"
              size="small"
              variant="contained"
              disableElevation={true}
              startIcon={<AddIcon />}
              onClick={redirectCreateLot}
            >
              Add Lot/Unit
            </Button>
          </GuardedComponent>
        )}
        <SiTrackGrid
          fileTitle='project_lot'
          columns={columns}
          dataItemKey="unitID"
          // queryConfig={unitsApiKeyStore.getProjectUnits(projectID)}
          // viewRoute={(value) => `/units/details/${value.unitID}`}
          viewRoute={(value) => `/units/profile/${value.unitID}`}
          CustomComponent={(value: any) => addWorkOrderElement(value)}
          actionPermissions={{
            view: UserPermissionName.VIEW_UNIT,
            export: UserPermissionName.DOWNLOAD_GRID,
          }}
          dataItems={filteredUnits || []}
          reorderable
          handleSearch={handleInputChange}
          handleSaveLayout={handleSaveLayout}
          isSaveLayout={true}
          gridConfigFromApi={gridConfig}
          handleResetLayout={handleResetLayout}
        // -----------------Grid Layout-------
        />
      </div>
      {isFormOpen && (
        <CreateWorkorderDialog
          open={isFormOpen}
          onClose={handleDialogClose}
          selectedFilters={{
            customerID: unitData?.customerID,
            officeID: unitData?.officeID,
            projectID: unitData?.projectID,
            unitID: unitData?.unitID,
          }}
        />
      )}
    </>
  );
};
